import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Slider,
  Alert,
  AlertTitle,
  Button,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  alpha,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Map as MapIcon,
  Schedule as ScheduleIcon,
  Route as RouteIcon,
  Visibility as VisibilityIcon,
  Speed as PerformanceIcon,
  Palette as PaletteIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  SettingsBrightness as SystemModeIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Public as WorldwideIcon,
  Place as RegionIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  People as PeopleIcon,
  Shield as ShieldIcon,
  Analytics as AnalyticsIcon,
  RemoveRedEye as ViewerIcon,
  Language as GlobeIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useSettings } from '../contexts/SettingsContext';
import CustomThemeDialog from '../components/settings/CustomThemeDialog';
import RegionsMapPreview, { getRegionColor } from '../components/settings/RegionsMapPreview';
import { colorSchemes } from '../theme/colorSchemes';
import apiService, { AdminConfigResponse, AzureADUser, AzureADGroup } from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const { 
    settings, 
    updateSpatial, 
    updateTemporal,
    updateRoutes,
    updateVisualization, 
    updatePerformance,
    updateAppearance,
  } = useSettings();

  // Custom themes state
  const [customThemes, setCustomThemes] = useState<any[]>([]);
  const [themeDialogOpen, setThemeDialogOpen] = useState(false);
  const [editingTheme, setEditingTheme] = useState<any>(null);
  const [loadingCustomThemes, setLoadingCustomThemes] = useState(false);

  // Administration state
  const [adminConfig, setAdminConfig] = useState<AdminConfigResponse | null>(null);
  const [loadingAdmin, setLoadingAdmin] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [selectedMapRegion, setSelectedMapRegion] = useState<string>('');

  // User Management state
  const [users, setUsers] = useState<AzureADUser[]>([]);
  const [azureGroups, setAzureGroups] = useState<AzureADGroup[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<AzureADUser | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [savingGroups, setSavingGroups] = useState(false);

  // Load custom themes from localStorage on mount (won't trigger API/auth)
  useEffect(() => {
    try {
      const stored = localStorage.getItem('custom_color_themes');
      if (stored) {
        setCustomThemes(JSON.parse(stored));
      }
    } catch (error) {
      console.warn('Failed to load custom themes from localStorage');
      setCustomThemes([]);
    }
  }, []);

  // Load admin config when Administration tab is selected
  useEffect(() => {
    if (activeTab === 7) { // Administration tab index
      loadAdminConfig();
    }
  }, [activeTab]);

  const loadAdminConfig = async () => {
    setLoadingAdmin(true);
    setAdminError(null);
    try {
      const config = await apiService.getAdminConfig();
      setAdminConfig(config);
      
      // If admin, also load users and groups
      if (config.current_user.is_admin) {
        loadUsers();
        loadAzureGroups();
      }
    } catch (error: any) {
      console.error('Failed to load admin config:', error);
      setAdminError(error.response?.data?.detail || 'Failed to load administration settings');
    } finally {
      setLoadingAdmin(false);
    }
  };

  const loadUsers = async (search?: string) => {
    setLoadingUsers(true);
    try {
      const response = await apiService.listUsers(search, 100);
      setUsers(response.users);
    } catch (error: any) {
      console.error('Failed to load users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadAzureGroups = async () => {
    try {
      const response = await apiService.listAzureGroups();
      setAzureGroups(response.groups);
    } catch (error: any) {
      console.error('Failed to load groups:', error);
    }
  };

  const handleEditUser = (user: AzureADUser) => {
    setSelectedUser(user);
    // Find group IDs from group names
    const groupIds = azureGroups
      .filter(g => user.groups.includes(g.displayName))
      .map(g => g.id);
    setSelectedGroups(groupIds);
    setEditDialogOpen(true);
  };

  const handleGroupToggle = (groupId: string) => {
    setSelectedGroups(prev => 
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  const handleSaveUserGroups = async () => {
    if (!selectedUser) return;
    
    setSavingGroups(true);
    try {
      await apiService.updateUserGroups(selectedUser.id, selectedGroups);
      
      // Optimistic update: Update the user's groups in local state immediately
      const newGroupNames = azureGroups
        .filter(g => selectedGroups.includes(g.id))
        .map(g => g.displayName);
      
      setUsers(prevUsers => 
        prevUsers.map(u => 
          u.id === selectedUser.id 
            ? { ...u, groups: newGroupNames }
            : u
        )
      );
      
      setEditDialogOpen(false);
      
      // Refresh both users and groups in background (for member counts)
      // Don't await - let it happen in background
      Promise.all([
        loadUsers(userSearch),
        loadAzureGroups()
      ]).catch(err => console.error('Background refresh failed:', err));
      
    } catch (error: any) {
      console.error('Failed to update groups:', error);
      alert('Failed to update user groups: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingGroups(false);
    }
  };

  const handleSearchUsers = () => {
    loadUsers(userSearch || undefined);
  };

  // Reload custom themes helper
  const reloadCustomThemes = async () => {
    try {
      const themes = await apiService.getCustomColorSchemes();
      setCustomThemes(themes);
      localStorage.setItem('custom_color_themes', JSON.stringify(themes));
    } catch (error) {
      console.error('Failed to reload themes:', error);
    }
  };

  // Custom theme handlers
  const handleCreateTheme = async (theme: any) => {
    await apiService.createCustomColorScheme(theme);
    await reloadCustomThemes();
    // Apply the new theme (find the newly created one)
    const themes = await apiService.getCustomColorSchemes();
    if (themes.length > 0) {
      updateAppearance({ colorScheme: `custom_${themes[themes.length - 1].id}` });
    }
  };

  const handleUpdateTheme = async (theme: any) => {
    if (editingTheme) {
      await apiService.updateCustomColorScheme(editingTheme.id, theme);
      await reloadCustomThemes();
      setEditingTheme(null);
    }
  };

  const handleDeleteTheme = async (themeId: number) => {
    if (window.confirm('Delete this custom theme?')) {
      await apiService.deleteCustomColorScheme(themeId);
      await reloadCustomThemes();
      // If deleted theme was active, switch to default
      if (settings.appearance.colorScheme === `custom_${themeId}`) {
        updateAppearance({ colorScheme: 'default' });
      }
    }
  };

  const handleEditTheme = (theme: any) => {
    setEditingTheme(theme);
    setThemeDialogOpen(true);
  };
  
  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <SettingsIcon sx={{ fontSize: 32, mr: 2, color: 'primary.main' }} />
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
        Analysis & Visualization Settings
      </Typography>
    </Box>
    <Typography variant="body2" color="text.secondary">
      Version: 0.11.3
    </Typography>
  </Box>


      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Customize route analysis parameters and visualization options. 
        Changes apply to new analyses and affect how data is processed and displayed.
      </Typography>

      {/* Settings Tabs */}
      <Paper sx={{ width: '100%', bgcolor: 'background.paper' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<PaletteIcon />} label="Appearance" />
          <Tab icon={<MapIcon />} label="Spatial Analysis" />
          <Tab icon={<ScheduleIcon />} label="Temporal Analysis" />
          <Tab icon={<RouteIcon />} label="Route Detection" />
          <Tab icon={<VisibilityIcon />} label="Map Visualization" />
          <Tab icon={<PerformanceIcon />} label="Performance" />
          <Tab icon={<AdminIcon />} label="Administration" />
        </Tabs>

        {/* Appearance Settings */}
        <TabPanel value={activeTab} index={0}>
          <Typography variant="h5" gutterBottom>Appearance Settings</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>About Appearance Settings</AlertTitle>
            Customize the visual appearance of the application including theme mode and color scheme.
          </Alert>

          <Grid container spacing={3}>
            {/* Theme Mode */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Theme Mode</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Choose between light mode, dark mode, or follow your system preferences.
                  </Typography>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Select Theme</FormLabel>
                    <RadioGroup
                      value={settings.appearance.themeMode}
                      onChange={(e) => updateAppearance({ themeMode: e.target.value as 'light' | 'dark' | 'system' })}
                    >
                      <FormControlLabel 
                        value="light" 
                        control={<Radio />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LightModeIcon />
                            <Typography>Light Mode</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel 
                        value="dark" 
                        control={<Radio />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DarkModeIcon />
                            <Typography>Dark Mode</Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel 
                        value="system" 
                        control={<Radio />} 
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SystemModeIcon />
                            <Typography>System Default</Typography>
                          </Box>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>

              {/* Theme Preview - Moved under Theme Mode */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Live Preview</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Current theme: <strong>{settings.appearance.themeMode === 'system' ? 'System (auto)' : settings.appearance.themeMode === 'light' ? 'Light' : 'Dark'}</strong>
                  </Typography>
                  <Box sx={{ 
                    p: 2.5, 
                    border: 1, 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                  }}>
                    <Typography variant="h6" color="primary" gutterBottom>OSINT Explorer</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Preview of theme colors and typography
                    </Typography>
                    
                    <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                      <Button variant="contained" size="small">Primary</Button>
                      <Button variant="outlined" size="small" color="secondary">Secondary</Button>
                      <Button variant="text" size="small">Text</Button>
                    </Stack>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                      <Chip label="Active" color="primary" size="small" />
                      <Chip label="Status" color="success" size="small" />
                      <Chip label="Warning" color="warning" size="small" />
                      <Chip label="Info" color="info" size="small" />
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Color Scheme Selector */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                      <Typography variant="h6">Color Scheme</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Select a preset or create your own custom theme
                      </Typography>
                    </Box>
                    <Tooltip title="Create Custom Theme">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        disabled={loadingCustomThemes}
                        onClick={async () => {
                          setLoadingCustomThemes(true);
                          try {
                            const themes = await apiService.getCustomColorSchemes();
                            setCustomThemes(themes);
                            localStorage.setItem('custom_color_themes', JSON.stringify(themes));
                          } catch (error: any) {
                            // Don't log out on 401 - just show empty themes
                            if (error?.response?.status !== 401) {
                              console.error('Could not load custom themes:', error);
                            }
                          } finally {
                            setLoadingCustomThemes(false);
                          }
                          setEditingTheme(null);
                          setThemeDialogOpen(true);
                        }}
                      >
                        {loadingCustomThemes ? 'Loading...' : 'Custom'}
                      </Button>
                    </Tooltip>
                  </Box>
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 1.5,
                    mb: 3
                  }}>
                    {colorSchemes.map((scheme) => {
                      const isSelected = settings.appearance.colorScheme === scheme.id;
                      return (
                        <Box
                          key={scheme.id}
                          onClick={() => updateAppearance({ colorScheme: scheme.id })}
                          sx={{
                            p: 1.5,
                            border: 2,
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            borderRadius: 1.5,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            bgcolor: 'transparent',
                            '&:hover': {
                              borderColor: 'primary.main',
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {/* Color Preview Dots */}
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    bgcolor: scheme.colors.primary,
                                    border: 1,
                                    borderColor: 'divider',
                                  }}
                                />
                                <Box
                                  sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '50%',
                                    bgcolor: scheme.colors.secondary,
                                    border: 1,
                                    borderColor: 'divider',
                                  }}
                                />
                              </Box>
                            </Box>
                            {isSelected && (
                              <CheckIcon color="primary" fontSize="small" />
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 500, mb: 0.5 }}>
                            {scheme.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {scheme.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Custom Themes */}
                  {customThemes.length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                        My Custom Themes
                      </Typography>
                      <Box sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                        gap: 1.5
                      }}>
                        {customThemes.map((theme) => {
                          const customId = `custom_${theme.id}`;
                          const isSelected = settings.appearance.colorScheme === customId;
                          return (
                            <Box
                              key={theme.id}
                              onClick={() => updateAppearance({ colorScheme: customId })}
                              sx={{
                                p: 1.5,
                                border: 2,
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                borderRadius: 1.5,
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                bgcolor: 'transparent',
                                position: 'relative',
                                '&:hover': {
                                  borderColor: 'primary.main',
                                  bgcolor: 'action.hover',
                                  '& .theme-actions': {
                                    opacity: 1
                                  }
                                },
                              }}
                            >
                              {/* Action buttons */}
                              <Box 
                                className="theme-actions"
                                sx={{ 
                                  position: 'absolute',
                                  top: 4,
                                  right: 4,
                                  display: 'flex',
                                  gap: 0.5,
                                  opacity: 0,
                                  transition: 'opacity 0.2s'
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleEditTheme(theme)}
                                  sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'action.hover' } }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDeleteTheme(theme.id)}
                                  sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'error.light' } }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {/* Color Preview Dots */}
                                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                                    <Box
                                      sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        bgcolor: theme.primary_color,
                                        border: 1,
                                        borderColor: 'divider',
                                      }}
                                    />
                                    <Box
                                      sx={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: '50%',
                                        bgcolor: theme.secondary_color,
                                        border: 1,
                                        borderColor: 'divider',
                                      }}
                                    />
                                  </Box>
                                </Box>
                                {isSelected && (
                                  <CheckIcon color="primary" fontSize="small" />
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: isSelected ? 600 : 500, mb: 0.5 }}>
                                {theme.scheme_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {theme.description || 'Custom theme'}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Custom Theme Dialog */}
          <CustomThemeDialog
            open={themeDialogOpen}
            onClose={() => {
              setThemeDialogOpen(false);
              setEditingTheme(null);
            }}
            onSave={editingTheme ? handleUpdateTheme : handleCreateTheme}
            existingTheme={editingTheme}
          />
        </TabPanel>

        {/* Spatial Analysis Settings */}
        <TabPanel value={activeTab} index={1}>
          <Typography variant="h5" gutterBottom>📍 Spatial Analysis Settings</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>About Spatial Settings</AlertTitle>
            Control how GPS points are grouped and how areas of activity are detected.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Grid Resolution</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Size of spatial grid cells for heatmap. Current: ~{Math.round(settings.spatial.gridResolution * 111000)}m cells
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.spatial.gridResolution}
                      onChange={(_, value) => updateSpatial({ gridResolution: value as number })}
                      min={0.0005}
                      max={0.005}
                      step={0.0001}
                      marks={[
                        { value: 0.0005, label: '~55m' },
                        { value: 0.001, label: '~110m' },
                        { value: 0.002, label: '~220m' },
                        { value: 0.005, label: '~550m' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `~${Math.round(value * 111000)}m`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Dwell Detection Radius</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Radius for detecting stops. Larger = easier detection, smaller = more precise.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.spatial.dwellRadius}
                      onChange={(_, value) => updateSpatial({ dwellRadius: value as number })}
                      min={10}
                      max={200}
                      step={5}
                      marks={[
                        { value: 10, label: '10m' },
                        { value: 50, label: '50m' },
                        { value: 100, label: '100m' },
                        { value: 200, label: '200m' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}m`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Temporal Analysis Settings */}
        <TabPanel value={activeTab} index={2}>
          <Typography variant="h5" gutterBottom>⏱️ Temporal Analysis Settings</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>About Temporal Settings</AlertTitle>
            Control time-based analysis parameters for dwell detection and activity periods.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Stay Time Threshold</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Minimum time to consider a location as a significant stop.
                    Current: {Math.round(settings.temporal.stayTimeThreshold / 60)} minutes
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.temporal.stayTimeThreshold}
                      onChange={(_, value) => updateTemporal({ stayTimeThreshold: value as number })}
                      min={60}
                      max={3600}
                      step={30}
                      marks={[
                        { value: 60, label: '1min' },
                        { value: 300, label: '5min' },
                        { value: 900, label: '15min' },
                        { value: 3600, label: '1hr' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.round(value / 60)}min`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Max Time Gap</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Maximum GPS signal gap before considering it a new session.
                    Current: {Math.round(settings.temporal.maxTimeGap / 60)} minutes
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.temporal.maxTimeGap}
                      onChange={(_, value) => updateTemporal({ maxTimeGap: value as number })}
                      min={300}
                      max={7200}
                      step={300}
                      marks={[
                        { value: 300, label: '5min' },
                        { value: 1200, label: '20min' },
                        { value: 3600, label: '1hr' },
                        { value: 7200, label: '2hr' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.round(value / 60)}min`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Route Detection Settings */}
        <TabPanel value={activeTab} index={3}>
          <Typography variant="h5" gutterBottom>🛣️ Route Detection Settings</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>About Route Settings</AlertTitle>
            Control how routes are detected and matched for pattern analysis.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Minimum Route Length</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Minimum GPS points required to consider as a valid route.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.routes.minRouteLength}
                      onChange={(_, value) => updateRoutes({ minRouteLength: value as number })}
                      min={3}
                      max={50}
                      step={1}
                      marks={[
                        { value: 3, label: '3' },
                        { value: 10, label: '10' },
                        { value: 25, label: '25' },
                        { value: 50, label: '50' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value} pts`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Route Similarity Threshold</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    How similar routes must be to group together. Lower = more groups.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.routes.similarityThreshold}
                      onChange={(_, value) => updateRoutes({ similarityThreshold: value as number })}
                      min={10}
                      max={90}
                      step={5}
                      marks={[
                        { value: 10, label: 'Strict' },
                        { value: 50, label: 'Normal' },
                        { value: 90, label: 'Loose' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}%`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Minimum Segment Distance</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Minimum meaningful distance for route segments.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.routes.minSegmentDistance}
                      onChange={(_, value) => updateRoutes({ minSegmentDistance: value as number })}
                      min={50}
                      max={1000}
                      step={25}
                      marks={[
                        { value: 50, label: '50m' },
                        { value: 250, label: '250m' },
                        { value: 500, label: '500m' },
                        { value: 1000, label: '1km' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => value >= 1000 ? `${(value/1000).toFixed(1)}km` : `${value}m`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Map Visualization Settings */}
        <TabPanel value={activeTab} index={4}>
          <Typography variant="h5" gutterBottom>🗺️ Map Visualization Settings</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>About Visualization Settings</AlertTitle>
            Control how heatmaps appear on the map. These affect visual appearance only.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Heatmap Visual Radius</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Size of heat circles. Larger = broader areas, smaller = precise spots.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.visualization.heatmapRadius}
                      onChange={(_, value) => updateVisualization({ heatmapRadius: value as number })}
                      min={10}
                      max={50}
                      step={1}
                      marks={[
                        { value: 10, label: '10px' },
                        { value: 20, label: '20px' },
                        { value: 35, label: '35px' },
                        { value: 50, label: '50px' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}px`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Minimum Heat Intensity</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sensitivity for low-activity areas. Lower = show faint activity.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.visualization.minIntensity}
                      onChange={(_, value) => updateVisualization({ minIntensity: value as number })}
                      min={0.001}
                      max={0.5}
                      step={0.001}
                      marks={[
                        { value: 0.001, label: 'Sensitive' },
                        { value: 0.1, label: 'Normal' },
                        { value: 0.3, label: 'Medium' },
                        { value: 0.5, label: 'High' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Track Point Size</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Size of GPS track points on the map. Larger = easier to see, smaller = less cluttered.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.visualization.trackPointSize ?? 4}
                      onChange={(_, value) => updateVisualization({ trackPointSize: value as number })}
                      min={1}
                      max={12}
                      step={1}
                      marks={[
                        { value: 1, label: '1px' },
                        { value: 4, label: '4px' },
                        { value: 8, label: '8px' },
                        { value: 12, label: '12px' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}px`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Link Analysis Settings */}
          <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>🔗 Link Analysis Settings</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>About Link Analysis Settings</AlertTitle>
            Control the appearance of the network graph in Link Analysis. These settings affect node sizes, connection styles, and graph layout.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Node Size</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Base size of nodes in the network graph. Selected nodes are 2x this size.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.visualization.linkAnalysis?.nodeSize ?? 8}
                      onChange={(_, value) => updateVisualization({ 
                        linkAnalysis: { 
                          ...settings.visualization.linkAnalysis, 
                          nodeSize: value as number 
                        } 
                      })}
                      min={4}
                      max={16}
                      step={1}
                      marks={[
                        { value: 4, label: '4px' },
                        { value: 8, label: '8px' },
                        { value: 12, label: '12px' },
                        { value: 16, label: '16px' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${value}px`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Link Distance</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Distance between connected nodes. Lower = tighter clusters.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.visualization.linkAnalysis?.linkDistance ?? 100}
                      onChange={(_, value) => updateVisualization({ 
                        linkAnalysis: { 
                          ...settings.visualization.linkAnalysis, 
                          linkDistance: value as number 
                        } 
                      })}
                      min={30}
                      max={250}
                      step={10}
                      marks={[
                        { value: 30, label: '30' },
                        { value: 100, label: '100' },
                        { value: 175, label: '175' },
                        { value: 250, label: '250' },
                      ]}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Charge Strength</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Node repulsion force. More negative = nodes push apart more.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.visualization.linkAnalysis?.chargeStrength ?? -300}
                      onChange={(_, value) => updateVisualization({ 
                        linkAnalysis: { 
                          ...settings.visualization.linkAnalysis, 
                          chargeStrength: value as number 
                        } 
                      })}
                      min={-800}
                      max={-50}
                      step={10}
                      marks={[
                        { value: -800, label: 'Strong' },
                        { value: -300, label: 'Default' },
                        { value: -50, label: 'Weak' },
                      ]}
                      valueLabelDisplay="auto"
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Link Curvature</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Amount of curve on connection lines. 0 = straight, 1 = very curved.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.visualization.linkAnalysis?.linkCurvature ?? 0.3}
                      onChange={(_, value) => updateVisualization({ 
                        linkAnalysis: { 
                          ...settings.visualization.linkAnalysis, 
                          linkCurvature: value as number 
                        } 
                      })}
                      min={0}
                      max={1}
                      step={0.1}
                      marks={[
                        { value: 0, label: 'Straight' },
                        { value: 0.5, label: 'Curved' },
                        { value: 1, label: 'Very Curved' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => value.toFixed(1)}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Link Opacity</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Transparency of connection lines. Lower = more see-through.
                  </Typography>
                  <Box sx={{ px: 1.5 }}>
                    <Slider
                      value={settings.visualization.linkAnalysis?.linkOpacity ?? 0.8}
                      onChange={(_, value) => updateVisualization({ 
                        linkAnalysis: { 
                          ...settings.visualization.linkAnalysis, 
                          linkOpacity: value as number 
                        } 
                      })}
                      min={0.1}
                      max={1}
                      step={0.1}
                      marks={[
                        { value: 0.1, label: 'Faint' },
                        { value: 0.5, label: '50%' },
                        { value: 1, label: 'Solid' },
                      ]}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Performance Settings */}
        <TabPanel value={activeTab} index={5}>
          <Typography variant="h5" gutterBottom>⚡ Performance Settings</Typography>
          <Alert severity="info" sx={{ mb: 3 }}>
            <AlertTitle>About Performance Settings</AlertTitle>
            Control processing limits and optimization settings for better analysis speed.
          </Alert>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Max Dwell Locations</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Maximum dwell locations to process (-1 for unlimited).
                  </Typography>
                  <Slider
                    value={settings.performance.maxDwellLocations}
                    onChange={(_, value) => updatePerformance({ maxDwellLocations: value as number })}
                    min={10}
                    max={1000}
                    step={10}
                    marks={[
                      { value: 10, label: '10' },
                      { value: 100, label: '100' },
                      { value: 500, label: '500' },
                      { value: 1000, label: '1000' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Max Routes</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Maximum routes to process and display.
      </Typography>
                  <Slider
                    value={settings.performance.maxRoutes}
                    onChange={(_, value) => updatePerformance({ maxRoutes: value as number })}
                    min={10}
                    max={1000}
                    step={10}
                    marks={[
                      { value: 10, label: '10' },
                      { value: 100, label: '100' },
                      { value: 500, label: '500' },
                      { value: 1000, label: '1000' },
                    ]}
                    valueLabelDisplay="auto"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Administration Settings */}
        <TabPanel value={activeTab} index={6}>
          <Typography variant="h5" gutterBottom>Administration</Typography>


          {loadingAdmin ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : adminError ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              <AlertTitle>Error</AlertTitle>
              {adminError}
            </Alert>
          ) : adminConfig ? (
            <Grid container spacing={3}>
              {/* Current User Info - Compact */}
              <Grid item xs={12}>
                <Card sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <CardContent sx={{ py: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                          {adminConfig.current_user.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {adminConfig.current_user.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {adminConfig.current_user.email}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" sx={{ gap: 0.75 }}>
                        {adminConfig.current_user.roles.map((role) => {
                          const roleConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string; label: string }> = {
                            administrator: { 
                              icon: <ShieldIcon sx={{ fontSize: 14 }} />, 
                              color: '#fff', 
                              bgColor: '#dc2626',
                              label: 'Admin'
                            },
                            user_access_administrator: { 
                              icon: <AdminIcon sx={{ fontSize: 14 }} />, 
                              color: '#fff', 
                              bgColor: '#7c3aed',
                              label: 'User Admin'
                            },
                            analyst: { 
                              icon: <AnalyticsIcon sx={{ fontSize: 14 }} />, 
                              color: '#fff', 
                              bgColor: '#2563eb',
                              label: 'Analyst'
                            },
                            viewer: { 
                              icon: <ViewerIcon sx={{ fontSize: 14 }} />, 
                              color: '#374151', 
                              bgColor: '#e5e7eb',
                              label: 'Viewer'
                            },
                          };
                          const config = roleConfig[role] || { 
                            icon: <PersonIcon sx={{ fontSize: 14 }} />, 
                            color: '#374151', 
                            bgColor: '#e5e7eb',
                            label: role
                          };
                          return (
                            <Box
                              key={role}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: '6px',
                                bgcolor: config.bgColor,
                                color: config.color,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                letterSpacing: '0.025em',
                                textTransform: 'uppercase',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                              }}
                            >
                              {config.icon}
                              {config.label}
                            </Box>
                          );
                        })}
                        {adminConfig.current_user.geographic_access?.worldwide_access && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              px: 1.25,
                              py: 0.5,
                              borderRadius: '6px',
                              bgcolor: '#059669',
                              color: '#fff',
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              letterSpacing: '0.025em',
                              textTransform: 'uppercase',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                            }}
                          >
                            <GlobeIcon sx={{ fontSize: 14 }} />
                            Global Access
                          </Box>
                        )}
                        {!adminConfig.current_user.geographic_access?.worldwide_access && 
                          adminConfig.current_user.groups.filter(g => g.startsWith('Tracer-')).map((group) => (
                            <Box
                              key={group}
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: '6px',
                                bgcolor: alpha(theme.palette.info.main, 0.1),
                                color: theme.palette.info.main,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                letterSpacing: '0.025em',
                                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                              }}
                            >
                              <RegionIcon sx={{ fontSize: 14 }} />
                              {group.replace('Tracer-', '')}
                            </Box>
                          ))
                        }
                      </Stack>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* User Management - Admin Only */}
              {adminConfig.current_user.is_admin && (
                <Grid item xs={12}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PeopleIcon sx={{ color: theme.palette.primary.main }} />
                          <Typography variant="h6">User Management</Typography>
                          <Box
                            sx={{
                              px: 0.75,
                              py: 0.125,
                              borderRadius: '4px',
                              bgcolor: alpha(theme.palette.primary.main, 0.15),
                              color: theme.palette.primary.main,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          >
                            {userSearch.trim() 
                              ? `${users.filter(u => 
                                  u.displayName.toLowerCase().includes(userSearch.toLowerCase()) ||
                                  u.mail?.toLowerCase().includes(userSearch.toLowerCase()) ||
                                  u.userPrincipalName?.toLowerCase().includes(userSearch.toLowerCase())
                                ).length} / ${users.length}`
                              : users.length
                            }
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <TextField
                            size="small"
                            placeholder="Search by name or email..."
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleSearchUsers()}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon sx={{ fontSize: 20 }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={{ width: 280 }}
                          />
                          <Button
                            variant="outlined"
                            onClick={handleSearchUsers}
                            disabled={loadingUsers}
                          >
                            Search
                          </Button>
                          <Tooltip title="Refresh all users and groups">
                            <IconButton 
                              onClick={() => { 
                                setUserSearch(''); 
                                loadUsers(); 
                                loadAzureGroups();
                              }} 
                              disabled={loadingUsers}
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      {loadingUsers ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                          <CircularProgress />
                        </Box>
                      ) : users.length === 0 ? (
                        <Alert severity="info">
                          No users found. Use the search box to find users by name or email, or click Refresh to load all users.
                        </Alert>
                      ) : users.filter((u) => {
                          if (!userSearch.trim()) return true;
                          const search = userSearch.toLowerCase();
                          return u.displayName.toLowerCase().includes(search) || 
                                 u.mail?.toLowerCase().includes(search) ||
                                 u.userPrincipalName?.toLowerCase().includes(search);
                        }).length === 0 ? (
                        <Alert severity="info">
                          No users match "{userSearch}". Try a different search term.
                        </Alert>
                      ) : (
                        <TableContainer sx={{ maxHeight: 400 }}>
                          <Table size="small" stickyHeader>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Department</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Geographic Access</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {users
                                .filter((user) => {
                                  if (!userSearch.trim()) return true;
                                  const search = userSearch.toLowerCase();
                                  return (
                                    user.displayName.toLowerCase().includes(search) ||
                                    (user.mail?.toLowerCase().includes(search)) ||
                                    (user.userPrincipalName?.toLowerCase().includes(search)) ||
                                    (user.department?.toLowerCase().includes(search))
                                  );
                                })
                                .map((user) => (
                                <TableRow 
                                  key={user.id}
                                  hover
                                  sx={{ 
                                    opacity: user.accountEnabled ? 1 : 0.5,
                                    bgcolor: user.groups.includes('Tracer-Worldwide') 
                                      ? alpha(theme.palette.success.main, 0.05) 
                                      : 'transparent'
                                  }}
                                >
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.8rem', bgcolor: theme.palette.grey[500] }}>
                                        {user.displayName.charAt(0)}
                                      </Avatar>
                                      <Typography variant="body2" fontWeight={500}>
                                        {user.displayName}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {user.mail || user.userPrincipalName}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {user.department || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    {user.groups.length === 0 ? (
                                      <Box
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: '4px',
                                          bgcolor: alpha('#ef4444', 0.1),
                                          color: '#ef4444',
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          letterSpacing: '0.02em',
                                          border: `1px solid ${alpha('#ef4444', 0.3)}`,
                                        }}
                                      >
                                        No Access
                                      </Box>
                                    ) : user.groups.includes('Tracer-Worldwide') ? (
                                      <Box
                                        sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          px: 1,
                                          py: 0.25,
                                          borderRadius: '4px',
                                          bgcolor: '#059669',
                                          color: '#fff',
                                          fontSize: '0.7rem',
                                          fontWeight: 600,
                                          letterSpacing: '0.02em',
                                          boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        }}
                                      >
                                        <GlobeIcon sx={{ fontSize: 12 }} />
                                        Worldwide
                                      </Box>
                                    ) : (
                                      <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                                        {user.groups.slice(0, 3).map((group) => (
                                          <Box 
                                            key={group}
                                            sx={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              gap: 0.25,
                                              px: 0.75,
                                              py: 0.25,
                                              borderRadius: '4px',
                                              bgcolor: alpha(theme.palette.info.main, 0.1),
                                              color: theme.palette.info.main,
                                              fontSize: '0.65rem',
                                              fontWeight: 600,
                                              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                            }}
                                          >
                                            <RegionIcon sx={{ fontSize: 10 }} />
                                            {group.replace('Tracer-', '')}
                                          </Box>
                                        ))}
                                        {user.groups.length > 3 && (
                                          <Box
                                            sx={{
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              px: 0.75,
                                              py: 0.25,
                                              borderRadius: '4px',
                                              bgcolor: alpha(theme.palette.grey[500], 0.15),
                                              color: theme.palette.text.secondary,
                                              fontSize: '0.65rem',
                                              fontWeight: 600,
                                            }}
                                          >
                                            +{user.groups.length - 3}
                                          </Box>
                                        )}
                                      </Stack>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Tooltip title="Edit Access">
                                      <IconButton 
                                        size="small" 
                                        onClick={() => handleEditUser(user)}
                                        sx={{ color: theme.palette.primary.main }}
                                      >
                                        <EditIcon sx={{ fontSize: 18 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Available Groups Reference */}
              <Grid item xs={12} md={adminConfig.current_user.is_admin ? 6 : 12} sx={{ display: 'flex' }}>
                <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <GroupIcon sx={{ color: theme.palette.info.main }} />
                      <Typography variant="h6">Available Access Groups</Typography>
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.125,
                          borderRadius: '4px',
                          bgcolor: alpha(theme.palette.info.main, 0.15),
                          color: theme.palette.info.main,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {azureGroups.length || adminConfig.all_groups.length}
                      </Box>
                    </Box>
                    
                    <TableContainer sx={{ flex: 1, maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600 }}>Group</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Regions</TableCell>
                            {adminConfig.current_user.is_admin && (
                              <TableCell sx={{ fontWeight: 600 }} align="right">Members</TableCell>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(azureGroups.length > 0 ? azureGroups : adminConfig.all_groups.map(g => ({ 
                            id: g.group_name, 
                            displayName: g.group_name, 
                            description: g.description,
                            memberCount: 0
                          }))).map((group: any) => {
                            const configGroup = adminConfig.all_groups.find(g => g.group_name === group.displayName);
                            return (
                              <TableRow key={group.id || group.displayName}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {configGroup?.worldwide_access ? (
                                      <WorldwideIcon sx={{ fontSize: 16, color: theme.palette.success.main }} />
                                    ) : (
                                      <RegionIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
                                    )}
                                    <Typography variant="body2" fontWeight={500}>
                                      {group.displayName.replace('Tracer-', '')}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  {configGroup?.worldwide_access ? (
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: '4px',
                                        bgcolor: alpha('#059669', 0.1),
                                        color: '#059669',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                      }}
                                    >
                                      <GlobeIcon sx={{ fontSize: 12 }} />
                                      All Regions
                                    </Box>
                                  ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                      {configGroup?.allowed_regions.join(', ') || '-'}
                                    </Typography>
                                  )}
                                </TableCell>
                                {adminConfig.current_user.is_admin && (
                                  <TableCell align="right">
                                    <Box
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: 28,
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: '4px',
                                        bgcolor: (group.memberCount || 0) > 0 
                                          ? alpha(theme.palette.success.main, 0.1)
                                          : alpha(theme.palette.grey[500], 0.1),
                                        color: (group.memberCount || 0) > 0
                                          ? theme.palette.success.main
                                          : theme.palette.text.disabled,
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        border: `1px solid ${(group.memberCount || 0) > 0 
                                          ? alpha(theme.palette.success.main, 0.2)
                                          : alpha(theme.palette.grey[500], 0.15)}`,
                                      }}
                                    >
                                      {group.memberCount || 0}
                                    </Box>
                                  </TableCell>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Card>
              </Grid>

              {/* Geographic Regions - Map with Dropdown */}
              <Grid item xs={12} md={adminConfig.current_user.is_admin ? 6 : 12} sx={{ display: 'flex' }}>
                <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                      <MapIcon sx={{ color: theme.palette.secondary.main }} />
                      <Typography variant="h6">Geographic Regions</Typography>
                      <Box
                        sx={{
                          px: 0.75,
                          py: 0.125,
                          borderRadius: '4px',
                          bgcolor: alpha(theme.palette.secondary.main, 0.15),
                          color: theme.palette.secondary.main,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }}
                      >
                        {adminConfig.all_regions.length}
                      </Box>
                      <Box sx={{ flex: 1 }} />
                      {/* Region Selector Dropdown */}
                      <FormControl size="small" sx={{ minWidth: 200 }}>
                        <Select
                          value={selectedMapRegion}
                          onChange={(e) => setSelectedMapRegion(e.target.value)}
                          displayEmpty
                          sx={{
                            bgcolor: alpha(theme.palette.background.paper, 0.5),
                            '& .MuiSelect-select': {
                              py: 0.75,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: alpha(theme.palette.divider, 0.3),
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                bgcolor: theme.palette.background.paper,
                              }
                            }
                          }}
                        >
                          <MenuItem value="">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <GlobeIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                              <Typography variant="body2" color="text.secondary">
                                All Regions
                              </Typography>
                            </Box>
                          </MenuItem>
                          {adminConfig.all_regions
                            .filter(r => r.bbox && r.bbox.length === 4)
                            .map((region, index) => (
                            <MenuItem key={region.name} value={region.name}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '2px',
                                    bgcolor: region.name === 'worldwide' 
                                      ? '#059669' 
                                      : getRegionColor(region.name, index),
                                    flexShrink: 0,
                                  }}
                                />
                                <Typography variant="body2">
                                  {region.description || region.name}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    
                    {/* Region Map */}
                    <Box sx={{ flex: 1, minHeight: 220 }}>
                      <RegionsMapPreview 
                        regions={adminConfig.all_regions} 
                        height={300} 
                        selectedRegion={selectedMapRegion}
                        onRegionClick={setSelectedMapRegion}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              {/* GAC Status */}
              <Grid item xs={12}>
                <Alert severity={adminConfig.gac_enabled ? 'success' : 'warning'}>
                  <AlertTitle>Geographic Access Control</AlertTitle>
                  {adminConfig.gac_enabled 
                    ? 'Geographic access control is ENABLED. Users can only query locations within their assigned regions.'
                    : 'Geographic access control is DISABLED. All authenticated users have worldwide access.'
                  }
                </Alert>
              </Grid>
            </Grid>
          ) : null}
        </TabPanel>

        {/* Edit User Dialog */}
        <Dialog 
          open={editDialogOpen} 
          onClose={() => setEditDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon />
              Edit User Access
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedUser && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, mt: 1 }}>
                  <Avatar sx={{ width: 48, height: 48, bgcolor: theme.palette.primary.main }}>
                    {selectedUser.displayName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6">{selectedUser.displayName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedUser.mail || selectedUser.userPrincipalName}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Geographic Access Groups:
                </Typography>
                
                <List dense sx={{ maxHeight: 300, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  {azureGroups.map((group) => {
                    const configGroup = adminConfig?.all_groups.find(g => g.group_name === group.displayName);
                    return (
                      <ListItem 
                        key={group.id}
                        button
                        onClick={() => handleGroupToggle(group.id)}
                        sx={{
                          bgcolor: selectedGroups.includes(group.id) 
                            ? alpha(theme.palette.primary.main, 0.1) 
                            : 'transparent'
                        }}
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={selectedGroups.includes(group.id)}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {configGroup?.worldwide_access ? (
                            <WorldwideIcon sx={{ color: theme.palette.success.main }} />
                          ) : (
                            <RegionIcon sx={{ color: theme.palette.info.main }} />
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={group.displayName.replace('Tracer-', '')}
                          secondary={configGroup?.worldwide_access 
                            ? 'Full worldwide access' 
                            : configGroup?.allowed_regions.join(', ')
                          }
                        />
                      </ListItem>
                    );
                  })}
                </List>

                {selectedGroups.length === 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    User will have NO geographic access if no groups are selected.
                  </Alert>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="contained" 
              onClick={handleSaveUserGroups}
              disabled={savingGroups}
              startIcon={savingGroups ? <CircularProgress size={16} /> : <SaveIcon />}
            >
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
};

export default SettingsPage;